import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useLocation } from 'react-router';

import { CookiesKeys } from '../enums/cookies-keys.enum';

import { IState } from '../models/reducers/i-state.interface';

import { setShowSelectWalletModal } from '../actions/global-actions';

import { useCookies } from '../hooks/useCookies';
import { useHomePageNavigate } from 'hooks/useHomePageNavigate';

interface IIsLoggedIn {
  children: React.ReactNode;
}

const IsLoggedIn = ({ children }: IIsLoggedIn): React.ReactElement | null => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { getCookie } = useCookies();
  const isLogged = getCookie(CookiesKeys.walletAddress);
  const { homePage } = useHomePageNavigate();
  const showSelectWalletModal = useSelector(({ global }: IState) => global.showSelectWalletModal);
  const [walletPromptOpened, setWalletPromptOpened] = useState(false);

  useEffect(() => {
    if (!isLogged) {
      dispatch(setShowSelectWalletModal(true));
    }
  }, [dispatch, isLogged]);

  useEffect(() => {
    if (showSelectWalletModal) {
      setWalletPromptOpened(true);
    }
  }, [showSelectWalletModal]);

  if (isLogged) {
    return <>{children}</>;
  }

  // While the wallet prompt is up, hold this route's URL instead of redirecting.
  // A `replace` redirect overwrites this history entry, so the entry is destroyed and
  // the browser Back button can never return here (it skips straight past it) - and
  // once the user connects they'd be left on the home page rather than the page they
  // asked for. Holding the URL means logging in simply renders `children` in place.
  if (!walletPromptOpened || showSelectWalletModal) {
    return null;
  }

  // Prompt was dismissed while still logged out: bounce home. `replace` is correct
  // here - the user opted out of logging in, so Back must not loop them into a page
  // they cannot view.
  return <Navigate to={homePage} state={{ from: location }} replace />;
};

export default IsLoggedIn;
